/**
 * Subscription Expiration Hook
 *
 * Shared hook for checking subscription expiration status across all pages.
 * Reads planDetails.subscriptionInfo (planEndDate, subscriptionStatus) directly
 * from the Redux app slice (state.app.planDetails). No need to pass data from
 * page facades.
 *
 * User/planDetails shape: planDetails.subscriptionInfo { planEndDate, subscriptionStatus, requiresAction, actionType }
 *
 * @package @ideoz/core
 */

import { useMemo } from 'react';
import { useAppSelector } from '../../../store/hooks';
import { isSubscriptionExpiringWithinDays, getDaysUntilExpiration, isSubscriptionExpired } from '../../../utils/subscription';

export interface UseSubscriptionExpirationParams {
  /**
   * Override: whether user has an active paid plan.
   * When not provided, derived from planDetails.name !== 'Free'.
   */
  hasActivePaidPlan?: boolean;
  /**
   * Override: next billing/expiration date (ISO string or Date).
   * When not provided, from planDetails.subscriptionInfo.planEndDate.
   */
  nextBillingDate?: string | Date | null;
}

export interface SubscriptionExpirationState {
  /**
   * Whether subscription is expiring within 3 days
   */
  isExpiringWithin3Days: boolean;
  /**
   * Formatted string like "1 day" or "2 days"
   */
  daysUntilExpiration: string;
  /**
   * Next billing/plan end date (ISO string or null)
   */
  nextBillingDate: string | null;
}

/**
 * Hook to check subscription expiration status.
 * Reads from Redux app slice: state.app.planDetails (from user data).
 * Uses planDetails.subscriptionInfo.planEndDate and planDetails.name.
 *
 * @param params - Optional overrides for tests; when omitted, uses Redux data
 * @returns Subscription expiration state
 */
export function useSubscriptionExpiration(
  params?: UseSubscriptionExpirationParams
): SubscriptionExpirationState {
  const planDetails = useAppSelector((state: any) => state.app.planDetails);

  // Derive from user/planDetails when not overridden
  const hasActivePaidPlan =
    params?.hasActivePaidPlan ??
    (planDetails?.name != null && planDetails.name !== 'Free');
  const nextBillingDate =
    params?.nextBillingDate ??
    (planDetails?.subscriptionInfo?.planEndDate ?? null);

  return useMemo(() => {
    if (!hasActivePaidPlan || !nextBillingDate) {
      return {
        isExpiringWithin3Days: false,
        daysUntilExpiration: '',
        nextBillingDate: null,
      };
    }

    const isExpiringSoon = isSubscriptionExpiringWithinDays(nextBillingDate, 3);
    const isExpired = isSubscriptionExpired(nextBillingDate);

    if (isExpired) {
      return {
        isExpiringWithin3Days: false,
        daysUntilExpiration: '',
        nextBillingDate: typeof nextBillingDate === 'string' ? nextBillingDate : nextBillingDate.toISOString(),
      };
    }

    return {
      isExpiringWithin3Days: isExpiringSoon,
      daysUntilExpiration: getDaysUntilExpiration(nextBillingDate),
      nextBillingDate: typeof nextBillingDate === 'string' ? nextBillingDate : nextBillingDate.toISOString(),
    };
  }, [hasActivePaidPlan, nextBillingDate]);
}
